import { useState } from 'react';
import { Abgangsleitung, AnyNetworkElement, isTrafoStation, isKabelverteilschrank, isHausanschluss, isMuffe } from '../../../types/network';
import { useNetworkElementsStore } from '../../../store/networkElementsStore';
import { useStressTestStore } from '../../../store/stressTestStore';
import { MeasureEditor } from '../../scenario/MeasureEditor';
import { useViewMode } from '../../../context/useViewMode';
import { useSettingsStore } from '../../../store/settingsStore';
import { ChevronIcon } from '../../icons/ButtonIcons';
import { ElementHistoryModal } from './shared/ElementHistoryModal';
import { OperationalRatingFactorField } from './shared/OperationalRatingFactorField';
import { detailStyles, headerColors } from '../../../styles/detailStyles';
import { fmt } from '../../../utils/numberFormat';
import { cableDisplayName, cableTypeLabel, shortElementName } from '../../../utils/elementDisplayName';
import { maxPhaseCurrentA } from '../../../utils/phaseCurrent';

const getLoadColor = (loadPercentage: number, thresholds: { green: number; yellow: number; orange: number; red: number }) => {
  if (!thresholds) return 'bg-blue-50 dark:bg-blue-900/20';

  if (loadPercentage >= thresholds.red) return 'bg-red-50 dark:bg-red-900/20';
  if (loadPercentage >= thresholds.orange) return 'bg-orange-50 dark:bg-orange-900/20';
  if (loadPercentage >= thresholds.yellow) return 'bg-yellow-50 dark:bg-yellow-900/20';
  if (loadPercentage >= thresholds.green) return 'bg-green-50 dark:bg-green-900/20';
  return 'bg-blue-50 dark:bg-blue-900/20';
};

const getLoadTextColor = (loadPercentage: number, thresholds: { green: number; yellow: number; orange: number; red: number }) => {
  if (!thresholds) return 'text-blue-700 dark:text-blue-300';

  if (loadPercentage >= thresholds.red) return 'text-red-700 dark:text-red-300';
  if (loadPercentage >= thresholds.orange) return 'text-orange-700 dark:text-orange-300';
  if (loadPercentage >= thresholds.yellow) return 'text-yellow-700 dark:text-yellow-300';
  if (loadPercentage >= thresholds.green) return 'text-green-700 dark:text-green-300';
  return 'text-blue-700 dark:text-blue-300';
};

const findElementByBusIndex = (elements: AnyNetworkElement[], busIndex: number): AnyNetworkElement | undefined => {
  for (const el of elements) {
    if (isTrafoStation(el)) {
      if (el.busIndexLv === busIndex) return el;
    } else if (isKabelverteilschrank(el)) {
      if (el.busIndex === busIndex) return el;
      for (const bb of el.busbars || []) {
        if (bb.busIndex === busIndex) return el;
      }
    } else if (isHausanschluss(el)) {
      if (el.busIndex === busIndex) return el;
    } else if ('busIndex' in el && (el as any).busIndex === busIndex) {
      return el;
    }
  }
  return undefined;
};

interface AbgangsleitungDetailsProps {
  element: Abgangsleitung;
}

export const AbgangsleitungDetails = ({ element: initialElement }: AbgangsleitungDetailsProps) => {
  // Check view mode
  const viewMode = useViewMode();
  const isStressTestMode = viewMode === 'stress-test';

  // In stress-test mode, use isolated stress elements; otherwise use global store
  const stressElements = useStressTestStore(state => state.stressElements);
  const globalElements = useNetworkElementsStore(state => state.elements);
  const elements = isStressTestMode ? stressElements : globalElements;

  const element = (elements.find(el => el.id === initialElement.id) ?? initialElement) as Abgangsleitung;

  const segments = useNetworkElementsStore(state => state.segments);
  const thresholds = useSettingsStore(state => state.thresholds.cableLoadLevels);

  // Stress test data - results are stored per segment ID, not per cable ID
  const stressTestResults = useStressTestStore(state => state.stressTestResults);
  
  const [segmentsExpanded, setSegmentsExpanded] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  
  // Move the early return AFTER all hooks are called to fix React hooks violation
  if (!element) {
    console.error('[AbgangsleitungDetails] Element not found in store:', initialElement.id);
    return null;
  }
  
  const lineSegments = element.segments.map((segment) => {
    const segmentData = segments[segment.id] || {};

    // Use first and last waypoints as start/end coordinates if they exist
    const waypoints = segment.waypoints || [];
    const hasWaypoints = waypoints.length > 0;

    // Get stress test data for THIS specific segment (results are stored per segment ID)
    const segmentStressData = stressTestResults?.results?.[segment.id];

    // In stress test mode, NEVER fall back to live data — strict isolation
    const powerData = isStressTestMode
      ? {
          powerKW: segmentStressData?.powerKw ?? null,
          powerKVAR: segmentStressData?.powerKvar ?? null,
          powerKVA: segmentStressData?.powerKva ?? null,
          currentA: segmentStressData?.currentA ?? null,
          currentFromA: segmentStressData?.currentFromA ?? null,
          currentToA: segmentStressData?.currentToA ?? null,
          currentL1A: segmentStressData?.currentL1A ?? null,
          currentL2A: segmentStressData?.currentL2A ?? null,
          currentL3A: segmentStressData?.currentL3A ?? null,
          loadingPercent: segmentStressData?.loadingPercent ?? null,
          fromBusVoltagePu: segmentStressData?.fromBusVoltagePu ?? null,
          toBusVoltagePu: segmentStressData?.toBusVoltagePu ?? null,
          fromBusVoltageKv: segmentStressData?.fromBusVoltageKv ?? null,
          toBusVoltageKv: segmentStressData?.toBusVoltageKv ?? null,
        }
      : {
          powerKW: segmentData.powerKW,
          powerKVAR: segmentData.powerKVAR,
          powerKVA: segmentData.powerKVA,
          currentA: segmentData.currentA,
          currentFromA: segmentData.currentFromA,
          currentToA: segmentData.currentToA,
          currentL1A: segmentData.currentL1A,
          currentL2A: segmentData.currentL2A,
          currentL3A: segmentData.currentL3A,
          loadingPercent: segmentData.loadingPercent,
          fromBusVoltagePu: segmentData.fromBusVoltagePu,
          toBusVoltagePu: segmentData.toBusVoltagePu,
          fromBusVoltageKv: segmentData.fromBusVoltageKv,
          toBusVoltageKv: segmentData.toBusVoltageKv,
        };

    return {
      ...segment,
      startCoordinate: hasWaypoints ? waypoints[0] : segment.startCoordinate,
      endCoordinate: hasWaypoints ? waypoints[waypoints.length - 1] : segment.endCoordinate,
      waypoints: segment.waypoints,
      length: segment.length,
      ...powerData,
      calculatedAt: isStressTestMode ? null : (segmentData.calculatedAt ?? null),
    };
  });

  const cableType = cableTypeLabel(element);
  const maxPower = Math.max(...lineSegments.map(s => Math.abs(s.powerKW ?? 0)));
  const maxApparentPower = Math.max(...lineSegments.map(s => Math.abs(s.powerKVA ?? 0)));
  const maxLoadPercentage = Math.max(...lineSegments.map(s => Math.abs(s.loadingPercent ?? 0)));
  
  const validThresholds = thresholds;
  
  const getConnections = (): { name: string; type: 'trafo' | 'kvs' | 'haus' | 'muffe' | 'unknown' }[] => {
    const connections: { name: string; type: 'trafo' | 'kvs' | 'haus' | 'muffe' | 'unknown' }[] = [];

    elements.forEach(el => {
      if (isTrafoStation(el)) {
        if (element.sourceId === el.id || element.targetId === el.id) {
          connections.push({ name: shortElementName(el) ?? el.name, type: 'trafo' });
        }
      } else if (isKabelverteilschrank(el)) {
        if (element.sourceId === el.id || element.targetId === el.id) {
          connections.push({ name: shortElementName(el) ?? el.name, type: 'kvs' });
        }
      } else if (isHausanschluss(el)) {
        if (el.connectedLine?.id === element.id) {
          connections.push({ name: shortElementName(el) ?? el.name, type: 'haus' });
        }
      } else if (isMuffe(el)) {
        if (element.sourceId === el.id || element.targetId === el.id) {
          connections.push({ name: shortElementName(el) ?? el.name, type: 'muffe' });
        }
      }
    });

    return connections;
  };


  return (
    <div className={detailStyles.layout.section}>
      {/* Header with Delete Button */}
      <div className={`${detailStyles.header.wrapper} ${headerColors.abgangsleitung.bg}`}>
        <div className={detailStyles.header.content}>
          <div className="flex items-center space-x-3">
            <div className={detailStyles.header.iconBox}>
              <svg className={`${detailStyles.header.icon} ${headerColors.abgangsleitung.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className={detailStyles.header.title}>Kabel</h2>
              <p className={detailStyles.header.subtitle} title={element.name}>{cableDisplayName(element, id => elements.find(e => e.id === id))}</p>
            </div>
          </div>
        </div>
        <div className={`${detailStyles.header.id} ${headerColors.abgangsleitung.id}`}>ID: {element.id}</div>
      </div>
      
      {/* Power Flow Section */}
      <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 dark:bg-amber-500" />
        <div className="pl-4 pr-3 py-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="shrink-0 w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Leistungsfluss</span>
            {(() => {
              const latestCalc = Math.max(
                ...lineSegments.map(s => (s as any).calculatedAt ?? 0).filter((t: number) => t > 0)
              );
              if (!latestCalc || latestCalc <= 0) return null;
              const d = new Date(latestCalc);
              // Date matters: in historical playback this is the rewound
              // instant, which can be months in the past.
              return (
                <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto tabular-nums" title={d.toLocaleString('de-DE')}>
                  Berechnung: {d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} {d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </span>
              );
            })()}
          </div>
          {lineSegments.length > 0 ? (
            <div className="flex items-center gap-4">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <div className={`text-2xl font-bold ${getLoadTextColor(maxLoadPercentage, validThresholds)}`}>
                    {fmt((maxApparentPower || maxPower), 1)} <span className="text-sm font-normal">kVA</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Max. Scheinleistung</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${getLoadTextColor(maxLoadPercentage, validThresholds)}`}>
                    {fmt(maxLoadPercentage, 1)} <span className="text-sm font-normal">%</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Max. Auslastung</div>
                </div>
              </div>
              <div className={`shrink-0 w-14 h-14 rounded-full ${getLoadColor(maxLoadPercentage, validThresholds)} flex items-center justify-center border-2 ${
                maxLoadPercentage >= validThresholds.red ? 'border-red-500 dark:border-red-400' :
                maxLoadPercentage >= validThresholds.orange ? 'border-orange-500 dark:border-orange-400' :
                maxLoadPercentage >= validThresholds.yellow ? 'border-yellow-500 dark:border-yellow-400' :
                maxLoadPercentage >= validThresholds.green ? 'border-green-500 dark:border-green-400' :
                'border-blue-500 dark:border-blue-400'
              }`}>
                <svg className={`w-7 h-7 ${getLoadTextColor(maxLoadPercentage, validThresholds)}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">
              Keine Leistungsflussdaten verfügbar
            </div>
          )}
        </div>
      </div>

      {/* Historical loadflow series from the snapshot archive (first segment as feeder reference) */}
      {element.segments.length > 0 && (
        <>
          <button
            onClick={() => setHistoryModalOpen(true)}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Lastfluss-Historie (Auslastung &amp; Leistung)
          </button>
          <ElementHistoryModal
            isOpen={historyModalOpen}
            elementId={element.segments[0].id}
            elementName={`${cableDisplayName(element, id => elements.find(e => e.id === id))} (Segment 1)`}
            showLoading
            showVoltage={false}
            onClose={() => setHistoryModalOpen(false)}
          />
        </>
      )}

      {/* Betriebsfaktor for the §14a trigger (control module only) */}
      {!isStressTestMode && (
        <OperationalRatingFactorField elementId={element.id} factor={element.operationalRatingFactor} ratingOf="des Nennstroms" />
      )}

      {/* Technical Details Section */}
      <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-cyan-400 dark:bg-cyan-500" />
        <div className="pl-4 pr-3 py-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="shrink-0 w-6 h-6 rounded-full bg-cyan-100 dark:bg-cyan-900/40 flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Technische Details</span>
          </div>

          {/* Cable Properties — Kabeltyp gets its own full-width row so long type names stay readable */}
          <div className="space-y-2 mb-3">
            <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-2 py-1.5">
              <div className="text-sm font-bold text-gray-900 dark:text-white break-all" title={String(cableType || 'N/A')}>{String(cableType || 'N/A')}</div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">Kabeltyp</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-2 py-1.5">
                <div className="text-sm font-bold text-gray-900 dark:text-white">{element.crossSection} <span className="text-xs font-normal">mm²</span></div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Querschnitt</div>
              </div>
              <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-2 py-1.5">
                <div className="text-sm font-bold text-gray-900 dark:text-white">{fmt(element.totalLength, 1)} <span className="text-xs font-normal">m</span></div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Gesamtlänge</div>
              </div>
            </div>
          </div>

          {/* Connected Elements */}
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Verbundene Elemente</div>
          {getConnections().length > 0 ? (
            <div className="space-y-1">
              {getConnections().map((conn, index) => (
                <div key={index} className="flex items-center bg-gray-50 dark:bg-primary-700/50 rounded-md px-2.5 py-1.5 gap-2">
                  {conn.type === 'trafo' && (
                    <div className="shrink-0 w-5 h-5 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                      <svg className="w-3 h-3 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  )}
                  {conn.type === 'kvs' && (
                    <div className="shrink-0 w-5 h-5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 flex items-center justify-center">
                      <svg className="w-3 h-3 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                  {conn.type === 'haus' && (
                    <div className="shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                  )}
                  {conn.type === 'muffe' && (
                    <div className="shrink-0 w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                      <svg className="w-3 h-3 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                  )}
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{conn.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">
              Keine verbundenen Elemente
            </div>
          )}
        </div>
      </div>

      {/* Grid measure: cable reinforcement (scenario only) */}
      {isStressTestMode && (
        <MeasureEditor elementId={element.id} kind="cable_reinforcement" current={element.cableTypeString || element.cableType} />
      )}

      {/* Cable Segments Section */}
      <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-400 dark:bg-blue-500" />
        <button
          onClick={() => setSegmentsExpanded(!segmentsExpanded)}
          className="flex items-center justify-between w-full text-left pl-4 pr-3 py-2.5 hover:bg-gray-50 dark:hover:bg-primary-700/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            <div className="shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Kabelsegmente</span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">{lineSegments.length}</span>
          </div>
          <ChevronIcon direction={segmentsExpanded ? "up" : "down"} className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        </button>

        {segmentsExpanded && (
        <div className="pl-4 pr-3 pb-3">
          <div className="space-y-2">
            {lineSegments.length > 0 ? (
              lineSegments.map((segment, index) => {
                const segLoad = Math.abs(segment.loadingPercent ?? 0);
                const loadColor = getLoadTextColor(segLoad, validThresholds);
                return (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-primary-700/50 rounded-md border border-gray-100 dark:border-primary-600 overflow-hidden"
                  >
                    {/* Segment header */}
                    <div className="flex justify-between items-center px-2.5 py-1.5 border-b border-gray-100 dark:border-primary-600">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Segment {index + 1}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">{fmt(segment.length, 1)}m</span>
                      </div>
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getLoadColor(segLoad, validThresholds)} ${loadColor}`}>{fmt(segLoad, 1)}%</span>
                    </div>
                    {/* Segment metrics */}
                    <div className="px-2.5 py-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className={`text-base font-bold ${loadColor}`}>
                            {fmt(Math.abs(segment.powerKVA ?? 0), 1)} <span className="text-xs font-normal">kVA</span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 flex gap-3">
                            <span>P: <span className="font-medium">{fmt(Math.abs(segment.powerKW ?? 0), 1)}</span> kW</span>
                            <span>Q: <span className="font-medium">{fmt((segment.powerKVAR ?? 0), 1)}</span> kvar</span>
                          </div>
                        </div>
                        <div>
                          {/* Headline current = highest phase conductor, the same one the
                              loading badge is built from. */}
                          <div className={`text-base font-bold ${loadColor}`}>
                            {fmt(maxPhaseCurrentA(segment), 1)} <span className="text-xs font-normal">A</span>
                            <span className="text-[10px] font-normal text-gray-400 dark:text-gray-500 ml-1">max. Phase</span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 flex gap-2 mt-0.5">
                            <span>L1: <span className="font-medium">{segment.currentL1A != null ? fmt(Math.abs(segment.currentL1A), 1) : '—'}</span></span>
                            <span>L2: <span className="font-medium">{segment.currentL2A != null ? fmt(Math.abs(segment.currentL2A), 1) : '—'}</span></span>
                            <span>L3: <span className="font-medium">{segment.currentL3A != null ? fmt(Math.abs(segment.currentL3A), 1) : '—'}</span></span>
                          </div>
                        </div>
                      </div>
                      {/* Segment power flow direction from topology bus indices */}
                      {(() => {
                        const fromBus = segment.fromBus;
                        const toBus = segment.toBus;
                        if (fromBus == null && toBus == null) return null;
                        const fromEl = fromBus != null ? findElementByBusIndex(elements, fromBus) : undefined;
                        const toEl = toBus != null ? findElementByBusIndex(elements, toBus) : undefined;
                        if (!fromEl && !toEl) return null;
                        return (
                          <div className="flex items-center text-xs text-purple-600 dark:text-purple-400 mt-1.5 pt-1.5 border-t border-gray-100 dark:border-primary-600">
                            <span className="truncate">
                              {fromEl?.name || `Bus ${fromBus}`} → {toEl?.name || `Bus ${toBus}`}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">
                Keine Segmente definiert
              </div>
            )}
          </div>
        </div>
        )}
      </div>

    </div>
  );
};
